import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Send, AlertCircle } from "lucide-react";
import Layout from "@/components/Layout";
import EmergencyToggle from "@/components/EmergencyToggle";
import FormLogo from "@/components/FormLogo";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Section, Field, FileField, TopNotice,
  ApplicantSpocSection, RequestedBySection, SelfSkipSection, makeUploader,
  isHodDropdownOptional, stripSelfSkipTransients, ResubmitFromSection,
} from "@/components/FormShell";

// Note: per spec, applicant must be a House Parent — enforced by seed roles.
// Front-end shows a soft advisory; backend accepts any authenticated user.
export default function NewPocketMoney() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [depts, setDepts] = useState([]);
  const [hods, setHods] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isResubmit, setIsResubmit] = useState(false);
  const [parentRefCode, setParentRefCode] = useState(null);
  const [form, setForm] = useState({
    staffName: user?.name || "", staffEmail: user?.email || "",
    staffContact: "",
    mainDept: "", subDept: "", hodApproval: "", is_hod_self: undefined, is_admin1_self: undefined, admin1_self_cost: "", admin1_self_remark: "",
    studentName: "", grade: "", amount: "", purpose: "", encl: "",
    is_emergency: false, emergency_reason: "",
  });

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/hods").then((r) => setHods(r.data)).catch(() => {});
  }, []);

  const subDepts = useMemo(() => {
    const d = depts.find((x) => x.name === form.mainDept);
    return d?.sub_departments || [];
  }, [depts, form.mainDept]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onUpload = makeUploader(api, setAttachments, setUploading, toast, formatApiError);

  const validate = () => {
    if (!(form.is_hod_self === true || form.is_admin1_self === true) && !String(form.hodApproval || "").trim()) {
      toast.error("Please select an HOD or declare Yes on Are you a HOD? / Admin-1?");
      return false;
    }
    for (const k of ["staffName", "staffEmail", "mainDept", "subDept",
                      "studentName", "grade", "amount", "purpose"]) {
      if (!String(form[k] || "").trim()) { toast.error(`Please fill: ${k}`); return false; }
    }
    if (form.is_emergency && (form.emergency_reason || "").trim().length < 10) {
      toast.error("Emergency Reason is mandatory (min 10 characters).");
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/requisitions/pocket_money", {
        form_type: "pocket_money",
        parent_ref_code: parentRefCode || undefined,
        form_data: { ...stripSelfSkipTransients(form), attachments },
      });
      toast.success(`Submitted — ${data.ref_code}`);
      nav(`/requisitions/${data.ref_code}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <Toaster position="bottom-right" richColors />
      <div className="max-w-5xl mx-auto relative">
        <FormLogo />
        <div className="mb-4 text-[11px] uppercase tracking-widest text-[#8a3f19] font-semibold">
          VGWS-PKT-2026 · Pocket Money Requisition
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight">
          Pocket Money Requisition (Imprest Account)
        </h1>
        <p className="text-sm text-[#4a524c] mt-1">
          Only House Parents may submit this form on behalf of a boarding student.
        </p>

        {/* Phase 7 — Pocket Money amber note banner */}
        <div className="mb-4 mt-4 rounded-md border-l-4 border-[#b48a45] bg-[#faf5eb] px-4 py-3 text-sm text-[#7a1f2b]"
             data-testid="pocket-money-note-banner">
          <strong>Note:</strong> The ideal timeline to submit this form is 7 working days prior to the requirement.
        </div>

        <EmergencyToggle
          isEmergency={form.is_emergency}
          onToggle={(v) => setForm((f) => ({ ...f, is_emergency: v, emergency_reason: v ? f.emergency_reason : "" }))}
          emergencyReason={form.emergency_reason}
          onReasonChange={(v) => setForm((f) => ({ ...f, emergency_reason: v }))}
        />

        <form className="mt-6 space-y-6" onSubmit={onSubmit} data-testid="pocket-money-form">
          <ResubmitFromSection
            isResubmit={isResubmit}
            onToggle={(v) => { setIsResubmit(v); if (!v) setParentRefCode(null); }}
            parentRefCode={parentRefCode}
            onSelectHistory={(fd, ref) => {
              setForm((f) => ({ ...f, ...fd,
                staffName: f.staffName || fd.staffName || "",
                staffEmail: f.staffEmail || fd.staffEmail || "",
              }));
              setParentRefCode(ref);
            }}
          />
          <ApplicantSpocSection form={form} setField={setField} />
          <SelfSkipSection form={form} setField={setField} userRole={user?.role} />
          <RequestedBySection form={form} setField={setField} depts={depts} hods={hods} subDepts={subDepts} hodOptional={isHodDropdownOptional(form)} />

          <Section numeral="III" title="Student details">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Student's Name" required>
                <input className="input-vgws" required value={form.studentName}
                       onChange={(e) => setField("studentName", e.target.value)}
                       data-testid="field-studentName" />
              </Field>
              <Field label="Grade" required>
                <input className="input-vgws" required value={form.grade}
                       onChange={(e) => setField("grade", e.target.value)}
                       data-testid="field-grade" />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Please permit me to withdraw Rs." required>
                <input type="number" min="1" className="input-vgws" required value={form.amount}
                       onChange={(e) => setField("amount", e.target.value)}
                       data-testid="field-amount" />
              </Field>
              <Field label="Encl:">
                <input className="input-vgws" value={form.encl}
                       onChange={(e) => setField("encl", e.target.value)}
                       data-testid="field-encl" />
              </Field>
            </div>
            <Field label="For the purpose of" required>
              <textarea className="input-vgws min-h-[80px]" required value={form.purpose}
                        onChange={(e) => setField("purpose", e.target.value)}
                        data-testid="field-purpose" />
            </Field>
          </Section>

          <Section numeral="IV" title="Attachments">
            <FileField label="Supporting documents" fieldName="supporting"
                       accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                       attachments={attachments} onChange={(e) => onUpload("supporting", e)}
                       disabled={uploading} testid="attach-supporting" optional />
          </Section>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <p className="text-xs text-[#6e7770] max-w-md">
              Fields marked <span className="text-[#b3261e]">*</span> are required.
            </p>
            <button type="submit" disabled={submitting} className="btn-vgws"
                    data-testid="submit-pocket-money-button">
              <Send size={16} /> {submitting ? "Submitting…" : "Submit requisition"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
